import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Send, Users, Loader2, X, AlertCircle, Heart, Volume2 } from "lucide-react";
import Hls from "hls.js";

const LOGO = "/upwardcast-logo-transparent.png";

function fmtClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export default function WatchRoom() {
  const { registrantId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [payState, setPayState] = useState(sessionId ? "checking" : "done");
  const [elapsed, setElapsed] = useState(0);
  const [visibleChat, setVisibleChat] = useState([]);
  const [activeCta, setActiveCta] = useState(null);
  const [dismissedCtas, setDismissedCtas] = useState([]);
  const [userMessages, setUserMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [muted, setMuted] = useState(true); // start muted so autoplay works even from a fresh email-link visit

  const offsetRef = useRef(0);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const chatEndRef = useRef(null);
  const syncedRef = useRef(false);

  const setVideoEl = useCallback((node) => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    videoRef.current = node;
    if (!node || !room?.webinar?.video_url) return;
    const url = room.webinar.video_url;
    syncedRef.current = false;
    if (url.endsWith(".m3u8") && !node.canPlayType("application/vnd.apple.mpegurl") && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(node);
      hlsRef.current = hls;
    } else {
      node.src = url;
    }
  }, [room]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") return setPayState("done");
        if (res.data.status === "expired" || attempts >= 8) return setPayState("failed");
      } catch {
        if (attempts >= 8) return setPayState("failed");
      }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const loadRoom = useCallback(async () => {
    try {
      const res = await api.get(`/public/room/${registrantId}`);
      setRoom(res.data);
      const serverNow = new Date(res.data.server_time).getTime() / 1000;
      offsetRef.current = serverNow - Date.now() / 1000;
      setViewerCount(32 + Math.floor(Math.random() * 40));
    } catch (err) {
      setError(err.response?.status === 402 ? "payment" : "notfound");
    }
  }, [registrantId]);

  useEffect(() => {
    if (payState === "done") loadRoom();
  }, [payState, loadRoom]);

  useEffect(() => {
    if (!room || room.is_replay) return;
    const start = new Date(room.session_start).getTime() / 1000;
    let id;
    const tick = () => {
      const nowServer = Date.now() / 1000 + offsetRef.current;
      const el = nowServer - start;
      const dur = room.webinar.duration_seconds;
      if (el >= dur) {
        // Session has ended: clamp the counter, freeze chat, stop ticking, end the room.
        setElapsed(dur);
        setVisibleChat(room.webinar.chat_messages);
        setActiveCta(null);
        if (id) clearInterval(id);
        return;
      }
      setElapsed(el);
      setVisibleChat(room.webinar.chat_messages.filter((m) => m.time_seconds <= el));
      const cta = [...room.webinar.ctas]
        .reverse()
        .find((c) => c.time_seconds <= el && (c.duration_seconds === 0 || el < c.time_seconds + c.duration_seconds));
      setActiveCta(cta || null);
      if (el > 0 && Math.random() < 0.2)
        setViewerCount((v) => Math.max(20, v + (Math.random() < 0.82 ? 1 : -1)));
    };
    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [room]);

  // Replay is on-demand: show the full scripted chat log, no live timer.
  useEffect(() => {
    if (room?.is_replay) setVisibleChat(room.webinar.chat_messages);
  }, [room]);

  useEffect(() => {
    if (!room || !videoRef.current || room.is_replay) return;
    const v = videoRef.current;
    v.muted = muted;
    if (!syncedRef.current && elapsed > 0 && elapsed < room.webinar.duration_seconds) {
      v.currentTime = elapsed;
      v.play().catch(() => {});
      syncedRef.current = true;
    } else if (!muted) {
      // unmuting is a user gesture: make sure playback is running with sound
      v.play().catch(() => {});
    }
    const onSeeking = () => {
      if (Math.abs(v.currentTime - elapsed) > 2 && v.currentTime > elapsed) v.currentTime = elapsed;
    };
    v.addEventListener("seeking", onSeeking);
    return () => v.removeEventListener("seeking", onSeeking);
  }, [room, elapsed, muted]);

  useEffect(() => {
    if (room?.is_replay) return; // replays keep the chat log scrolled to the top, no auto-jump
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [visibleChat, userMessages, room]);

  const sendMessage = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const vidTime = videoRef.current ? videoRef.current.currentTime : elapsed;
    setUserMessages((m) => [...m, { id: Date.now(), author: room.registrant.name || "You", message: text, me: true, at: elapsed }]);
    setDraft("");
    api.post(`/public/room/${registrantId}/message`, { message: text, video_seconds: Math.max(0, Math.floor(vidTime || 0)) })
      .catch(() => {});
  };

  if (payState === "checking")
    return <Center><Loader2 className="w-6 h-6 animate-spin text-[#16B6A4]" /><p className="text-[#9A95B5] mt-3">Confirming your reservation…</p></Center>;
  if (payState === "failed")
    return <Center><AlertCircle className="w-8 h-8 text-[#16B6A4]" /><p className="text-[#ECEAF7] mt-3">We couldn't confirm your payment.</p><Link to="/" className="text-[#16B6A4] mt-2">← Back home</Link></Center>;
  if (error === "payment")
    return <Center><AlertCircle className="w-8 h-8 text-[#16B6A4]" /><p className="text-[#ECEAF7] mt-3">This experience needs a reservation.</p></Center>;
  if (error === "notfound")
    return <Center><p className="text-[#ECEAF7]">This room could not be found.</p><Link to="/" className="text-[#16B6A4] mt-2">← Back home</Link></Center>;
  if (!room)
    return <Center><Loader2 className="w-6 h-6 animate-spin text-[#16B6A4]" /></Center>;

  const duration = room.webinar.duration_seconds;
  const notStarted = elapsed < 0;
  const ended = elapsed >= duration;
  const onScreenHosts = room.webinar.hosts.filter((h) => h.on_screen);
  const endScreen = room.webinar.end_screen || {};
  // Hide the live chat once a live session has ended (replays keep the chat log visible).
  const finished = !room.is_replay && ended;
  const allChat = [...visibleChat.map((m) => ({ ...m, me: false })), ...userMessages].sort(
    (a, b) => (a.at ?? a.time_seconds ?? 0) - (b.at ?? b.time_seconds ?? 0)
  );

  return (
    <div className="wellness h-screen overflow-hidden bg-[#161320] text-[#ECEAF7] flex flex-col" data-testid="watch-room">
      <header className="border-b border-white/5 px-4 sm:px-6 h-16 flex items-center justify-between shrink-0">
        <img src={LOGO} alt="UpwardCast" className="h-6 w-auto brightness-0 invert opacity-90" />
        <div className="flex items-center gap-5">
          {room.is_replay ? (
            <span className="text-xs uppercase tracking-[0.15em] text-[#9A95B5]" data-testid="replay-badge">Replay · On demand</span>
          ) : !notStarted && !ended ? (
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#16B6A4]">
              <span className="w-2 h-2 rounded-full bg-[#16B6A4] animate-pulse" /> Live now
            </span>
          ) : null}
          {!room.is_replay && (
            <span className="flex items-center gap-1.5 text-xs text-[#9A95B5]" data-testid="viewer-count">
              <Users className="w-3.5 h-3.5" /> {viewerCount} here now
            </span>
          )}
          <Link to="/" data-testid="exit-room-btn" className="flex items-center gap-1.5 text-xs text-[#9A95B5] hover:text-[#ECEAF7] border border-white/10 rounded-full px-3 py-1.5 transition-colors">
            <X className="w-3.5 h-3.5" /> Leave
          </Link>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Stage */}
        <div className="flex-1 flex flex-col min-h-0 p-3 sm:p-5">
          <div className="relative flex-1 bg-[#100E18] rounded-[1.5rem] overflow-hidden flex items-center justify-center min-h-[260px]">
            {room.is_replay ? (
              room.webinar.video_url ? (
                <video
                  ref={setVideoEl}
                  data-testid="replay-video"
                  className="w-full h-full max-h-[72vh] object-contain"
                  controls
                  playsInline
                  autoPlay
                />
              ) : (
                <FallbackStage hosts={onScreenHosts} title={room.webinar.title} />
              )
            ) : notStarted ? (
              <Countdown seconds={-elapsed} hosts={onScreenHosts} title={room.webinar.title} />
            ) : ended ? (
              endScreen.enabled ? (
                <EndScreenView data={endScreen} />
              ) : (
                <div className="text-center px-6" data-testid="ended-close">
                  <Heart className="w-8 h-8 text-[#16B6A4] mx-auto mb-3" strokeWidth={1.5} />
                  <h2 className="text-3xl font-light mb-2 text-[#F5F3FF]">Thank you for being here.</h2>
                  <p className="text-[#9A95B5]">This gathering has come to a close.</p>
                  {room.webinar.replay_enabled && <p className="text-[#6A6588] text-sm mt-2">A replay will be available from your account.</p>}
                  <Link to="/" data-testid="ended-exit" className="inline-block mt-6 text-sm text-[#9A95B5] hover:text-[#ECEAF7] transition-colors">Leave the room</Link>
                </div>
              )
            ) : room.webinar.video_url ? (
              <video
                ref={setVideoEl}
                data-testid="webinar-video"
                className="w-full h-full max-h-[72vh] object-contain"
                controls={false}
                playsInline
                autoPlay
                muted={muted}
              />
            ) : (
              <FallbackStage hosts={onScreenHosts} title={room.webinar.title} />
            )}

            {!notStarted && !ended && !room.is_replay && room.webinar.video_url && muted && (
              <button
                onClick={() => setMuted(false)}
                data-testid="unmute-btn"
                className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-[1px] transition-opacity"
              >
                <span className="bg-white text-[#161320] px-6 py-3.5 rounded-full font-semibold text-sm flex items-center gap-2 shadow-xl hover:scale-105 transition-transform">
                  <Volume2 className="w-5 h-5 text-[#7B2CBF]" /> Tap for sound
                </span>
              </button>
            )}

            {!notStarted && !ended && !room.is_replay && !muted && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div className="h-full brand-gradient transition-all" style={{ width: `${Math.min(100, (elapsed / duration) * 100)}%` }} />
              </div>
            )}

            {activeCta && !dismissedCtas.includes(activeCta.id) && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-md animate-cta-in" data-testid="active-cta">
                <div className="bg-white text-[#211C36] rounded-3xl p-6 soft-shadow relative">
                  <button
                    onClick={() => setDismissedCtas((d) => [...d, activeCta.id])}
                    className="absolute top-3 right-3 text-[#9A95B5] hover:text-[#211C36]"
                    data-testid="cta-dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#7B2CBF] mb-2">A gift for you</div>
                  <h3 className="font-display text-2xl leading-tight mb-1">{activeCta.headline}</h3>
                  {activeCta.description && <p className="text-sm text-[#6A6588] mb-4">{activeCta.description}</p>}
                  <a
                    href={activeCta.button_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cta-button"
                    className="inline-block btn-gradient text-white px-7 py-3 rounded-full font-medium text-sm"
                  >
                    {activeCta.button_text}
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="px-1 sm:px-2 pt-4 flex items-center justify-between shrink-0">
            <div>
              <h1 className="font-display text-2xl leading-tight text-[#F5F3FF] font-medium">{room.webinar.title}</h1>
              <div className="text-sm text-[#9A95B5] mt-0.5">with {onScreenHosts.map((h) => h.name).join(" & ")}</div>
            </div>
            <div className="font-mono text-sm text-[#9A95B5]">
              {room.is_replay ? "On demand" : notStarted ? "Beginning soon" : `${fmtClock(elapsed)} / ${fmtClock(duration)}`}
            </div>
          </div>
        </div>

        {/* Community chat */}
        {!finished && (
        <aside className="w-full lg:w-[360px] border-t lg:border-t-0 lg:border-l border-white/5 flex flex-col bg-[#1A1726] shrink-0 h-[380px] lg:h-auto">
          <div className="px-5 h-14 flex items-center gap-2 border-b border-white/5 text-sm tracking-wide text-[#9A95B5] shrink-0">
            <Users className="w-4 h-4 text-[#16B6A4]" /> Community
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-stream">
            {allChat.length === 0 && <p className="text-sm text-[#6A6588] text-center mt-10">The circle will fill as we begin. 🌿</p>}
            {allChat.map((m, i) => (
              <div key={(m.id || i) + "-" + i} className="animate-slide-up flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#2A2540] overflow-hidden flex items-center justify-center text-[11px] font-medium shrink-0 text-[#9A95B5]">
                  {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover" /> : (m.author || "?")[0]}
                </div>
                <div className="min-w-0">
                  <span className={`text-xs font-medium ${m.me ? "text-[#16B6A4]" : "text-[#ECEAF7]"}`}>{m.author}</span>
                  <p className="text-sm text-[#C9C5DD] break-words leading-relaxed">{m.message}</p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendMessage} className="border-t border-white/5 p-3 flex gap-2 shrink-0">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Share something kind…"
              data-testid="chat-input"
              className="flex-1 bg-[#241F38] text-white rounded-full px-4 py-2.5 text-sm placeholder-[#6A6588] focus:outline-none focus:ring-1 focus:ring-[#7B2CBF]"
            />
            <button type="submit" data-testid="chat-send-btn" className="btn-gradient w-10 h-10 rounded-full flex items-center justify-center shrink-0">
              <Send className="w-4 h-4 text-white" />
            </button>
          </form>
        </aside>
        )}
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="wellness min-h-screen bg-[#161320] flex flex-col items-center justify-center text-center px-6">{children}</div>;
}

function EndScreenView({ data }) {
  return (
    <div className="text-center px-6 w-full max-w-lg mx-auto animate-fade-up" data-testid="end-screen">
      {data.image_url && (
        <div className="overflow-hidden rounded-[1.5rem] mb-7 mx-auto max-w-sm">
          <img src={data.image_url} alt="" className="w-full h-48 object-cover" />
        </div>
      )}
      <h2 className="font-display text-4xl mb-4 text-[#F5F3FF]">{data.headline || "Thank you for being here."}</h2>
      {data.body && <p className="text-[#9A95B5] leading-relaxed mb-7 whitespace-pre-line">{data.body}</p>}
      {data.button_text && data.button_url && (
        <a
          href={data.button_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="end-screen-button"
          className="inline-block btn-gradient text-white px-8 py-3.5 rounded-full font-medium"
        >
          {data.button_text}
        </a>
      )}
      <div className="mt-8">
        <Link to="/" data-testid="end-screen-exit" className="text-sm text-[#9A95B5] hover:text-[#ECEAF7] transition-colors">Leave the room</Link>
      </div>
    </div>
  );
}

function Countdown({ seconds, hosts, title }) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="text-center px-6" data-testid="countdown">
      <div className="text-xs uppercase tracking-[0.25em] text-[#16B6A4] mb-5">Your session begins in</div>
      <div className="font-mono text-5xl sm:text-7xl font-light tracking-tight mb-7 text-[#ECEAF7]">
        {d > 0 && `${d}d `}{pad(h)}:{pad(m)}:{pad(s)}
      </div>
      <h2 className="font-display text-3xl mb-7 text-[#F5F3FF]">{title}</h2>
      <div className="flex items-center justify-center gap-5">
        {hosts.map((host) => (
          <div key={host.id} className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-[#2A2540] overflow-hidden flex items-center justify-center font-display text-xl text-[#9A95B5]">
              {host.avatar_url ? <img src={host.avatar_url} alt={host.name} className="w-full h-full object-cover" /> : host.name?.[0]}
            </div>
            <span className="text-sm text-[#9A95B5] mt-2">{host.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FallbackStage({ hosts, title }) {
  return (
    <div className="text-center px-6 w-full" data-testid="fallback-stage">
      <div className="flex items-center justify-center gap-8 mb-7">
        {(hosts.length ? hosts : [{ id: "x", name: "Host" }]).map((host) => (
          <div key={host.id} className="flex flex-col items-center animate-float-slow">
            <div className="w-28 h-28 rounded-full brand-gradient overflow-hidden flex items-center justify-center font-display text-3xl text-white">
              {host.avatar_url ? <img src={host.avatar_url} alt={host.name} className="w-full h-full object-cover" /> : host.name?.[0]}
            </div>
            <span className="text-base text-[#ECEAF7] mt-3">{host.name}</span>
            {host.title && <span className="text-xs text-[#9A95B5]">{host.title}</span>}
          </div>
        ))}
      </div>
      <h2 className="font-display text-3xl text-[#F5F3FF]">{title}</h2>
      <p className="text-[#6A6588] text-sm mt-2">Settling in together…</p>
    </div>
  );
}
